import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { getCoreRowModel } from '@tanstack/react-table';

import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

// Utils
import { Storage } from '../../../../utils/Storage';
import { makeDeviceTypeSettingColumn } from '../../../../utils/columns/makeDeviceTypeSettingColumn';

// Services
import { getAllDeviceTypes, updateDeviceTypeSettings } from '../../../../services/setting.service';

// Common
import SearchBar from '../../../common/SearchBar/SearchBar';
import CustomModal from '../../../common/withModal';
import { useStandardTable as useTable, Table } from '../../../common/table/index';
import { Spinner } from '../../../common/Spinner';

// Constants
import { DeviceTypeSettingsConstants, SearchBarFilterTypes } from '../../../../constants/Constants';
import { SettingsDeviceType } from '../../../../types/DeviceTypeSettings';

const DeviceTypeSettings = () => {
  const [paramFilter, setParamFilter] = useState<any>({});
  const [isEditableDeviceType, setEditableDeviceType] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [initialFormData, setInitialFormData] = useState({
    manufacturer: '',
    type: '',
    name: '',
    maxAgeInDays: 0,
    maxDaysSinceLastSighting: 0,
    minBatteryVoltage: 0,
  });

  const reloadDeviceTypeSettings = () => setParamFilter({ ...paramFilter, changedAt: new Date() });

  const defaultFilters = () => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.DEVICE_TYPES);
    if (filterState) {
      setParamFilter({
        filters: filterState.filters,
        sortBy: filterState.sortBy,
      });
    }
  };

  useEffect(() => {
    defaultFilters();
  }, []);

  const handleReset = () => {
    setParamFilter({ filters: [], sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.DEVICE_TYPES, { filters: [] });
  };

  const onSearchChange = (filters: any) => {
    setParamFilter({ filters: filters });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.DEVICE_TYPES, { isMoreFilter: true, filters: filters });
  };

  const handleEdit = (data: SettingsDeviceType) => {
    setEditableDeviceType(data.id);
    setInitialFormData({
      manufacturer: data.manufacturer || '',
      type: data.type || '',
      name: data.name || '',
      maxAgeInDays: data.settings.isHealthy_MaxAgeInDays,
      maxDaysSinceLastSighting: data.settings.isHealthy_MaxDaysSinceLastSighting,
      minBatteryVoltage: data.settings.isHealthy_MinBatteryVoltage,
    });
  };

  const columns = useMemo(() => makeDeviceTypeSettingColumn(handleEdit), []);

  const table = useTable<any>({
    columns,
    fetchAsync: getAllDeviceTypes,
    params: paramFilter,
    serverSide: true,
    shouldFetch: true,
  });

  const validationSchema = Yup.object().shape({
    manufacturer: Yup.string().required(DeviceTypeSettingsConstants.validationRequired),
    type: Yup.string().required(DeviceTypeSettingsConstants.validationRequired),
    name: Yup.string().required(DeviceTypeSettingsConstants.validationRequired),
    maxAgeInDays: Yup.number()
      .required(DeviceTypeSettingsConstants.validationRequired)
      .positive(DeviceTypeSettingsConstants.validationPositive)
      .integer(DeviceTypeSettingsConstants.validationInteger),
    maxDaysSinceLastSighting: Yup.number()
      .required(DeviceTypeSettingsConstants.validationRequired)
      .positive(DeviceTypeSettingsConstants.validationPositive)
      .integer(DeviceTypeSettingsConstants.validationInteger),
    minBatteryVoltage: Yup.number().required(DeviceTypeSettingsConstants.validationRequired).positive(DeviceTypeSettingsConstants.validationPositive),
  });

  const handleSubmit = async (values: any) => {
    const payload = {
      manufacturer: values.manufacturer,
      type: values.type,
      name: values.name,
      settings: {
        isHealthy_MaxAgeInDays: values.maxAgeInDays,
        isHealthy_MaxDaysSinceLastSighting: values.maxDaysSinceLastSighting,
        isHealthy_MinBatteryVoltage: values.minBatteryVoltage,
      },
    };

    setLoading(true);
    try {
      await updateDeviceTypeSettings(isEditableDeviceType, payload);
      reloadDeviceTypeSettings();
    } catch (error) {
      console.error('Error updating device type settings:', error);
    } finally {
      setLoading(false);
      setEditableDeviceType(undefined);
    }
  };

  const capitalizeLabels = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

  return (
    <>
      <SearchBar
        pageKey={SearchBarFilterTypes.DEVICE_TYPES}
        onSearch={onSearchChange}
        onReset={handleReset}
        columns={columns}
        allowSearchAll={true}
      />
      <CustomModal
        title={DeviceTypeSettingsConstants.title}
        showSaveButton={false}
        isOpen={!!isEditableDeviceType}
        setIsOpen={() => setEditableDeviceType(undefined)}
        maxWidth='sm'
      >
        <Formik initialValues={initialFormData} enableReinitialize={true} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              {/* Device Type */}
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor={DeviceTypeSettingsConstants.type}>{capitalizeLabels(DeviceTypeSettingsConstants.type)}</label>
                <Field
                  name={DeviceTypeSettingsConstants.type}
                  type='text'
                  placeholder={DeviceTypeSettingsConstants.type}
                  as={TextField}
                  fullWidth
                  variant='outlined'
                  id={DeviceTypeSettingsConstants.type}
                />
                <ErrorMessage name={DeviceTypeSettingsConstants.type}>{(msg) => <div style={{ color: 'red' }}>{msg}</div>}</ErrorMessage>
              </div>

              {/* Name */}
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor={DeviceTypeSettingsConstants.name}>{capitalizeLabels(DeviceTypeSettingsConstants.name)}</label>
                <Field
                  name={DeviceTypeSettingsConstants.name}
                  type='text'
                  placeholder={DeviceTypeSettingsConstants.name}
                  as={TextField}
                  fullWidth
                  variant='outlined'
                  id={DeviceTypeSettingsConstants.name}
                />
                <ErrorMessage name={DeviceTypeSettingsConstants.name}>{(msg) => <div style={{ color: 'red' }}>{msg}</div>}</ErrorMessage>
              </div>

              {/* Manufacturer */}
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor={DeviceTypeSettingsConstants.manufacturer}>{capitalizeLabels(DeviceTypeSettingsConstants.manufacturer)}</label>
                <Field
                  name={DeviceTypeSettingsConstants.manufacturer}
                  type='text'
                  placeholder={DeviceTypeSettingsConstants.manufacturer}
                  as={TextField}
                  fullWidth
                  variant='outlined'
                  id={DeviceTypeSettingsConstants.manufacturer}
                />
                <ErrorMessage name={DeviceTypeSettingsConstants.manufacturer}>{(msg) => <div style={{ color: 'red' }}>{msg}</div>}</ErrorMessage>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor={DeviceTypeSettingsConstants.maxAgeInDays}>{DeviceTypeSettingsConstants.maxAgeInDaysLabel}</label>
                <Field
                  name={DeviceTypeSettingsConstants.maxAgeInDays}
                  type='number'
                  placeholder={DeviceTypeSettingsConstants.placeholderMaxAgeInDays}
                  as={TextField}
                  fullWidth
                  variant='outlined'
                  id={DeviceTypeSettingsConstants.maxAgeInDays}
                />
                <ErrorMessage name={DeviceTypeSettingsConstants.maxAgeInDays}>{(msg) => <div style={{ color: 'red' }}>{msg}</div>}</ErrorMessage>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor={DeviceTypeSettingsConstants.maxDaysSinceLastSighting} style={{ width: '100%' }}>
                  {DeviceTypeSettingsConstants.maxDaysSinceLastSightingLabel}
                </label>
                <Field
                  name={DeviceTypeSettingsConstants.maxDaysSinceLastSighting}
                  type='number'
                  placeholder={DeviceTypeSettingsConstants.placeholderMaxDaysSinceLastSighting}
                  as={TextField}
                  fullWidth
                  variant='outlined'
                  id={DeviceTypeSettingsConstants.maxDaysSinceLastSighting}
                />
                <ErrorMessage name={DeviceTypeSettingsConstants.maxDaysSinceLastSighting}>
                  {(msg) => <div style={{ color: 'red' }}>{msg}</div>}
                </ErrorMessage>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor={DeviceTypeSettingsConstants.minBatteryVoltage}>{DeviceTypeSettingsConstants.minBatteryVoltageLabel}</label>
                <Field
                  name={DeviceTypeSettingsConstants.minBatteryVoltage}
                  type='number'
                  placeholder={DeviceTypeSettingsConstants.placeholderMinBatteryVoltage}
                  as={TextField}
                  fullWidth
                  variant='outlined'
                  id={DeviceTypeSettingsConstants.minBatteryVoltage}
                />
                <ErrorMessage name={DeviceTypeSettingsConstants.minBatteryVoltage}>{(msg) => <div style={{ color: 'red' }}>{msg}</div>}</ErrorMessage>
              </div>

              <Button type='submit' variant='contained' color='primary' disabled={loading}>
                {loading ? 'Saving...' : DeviceTypeSettingsConstants.saveButtonLabel}
              </Button>
            </Form>
          )}
        </Formik>
      </CustomModal>
      <Spinner loading={loading} />
      <Paper>
        <Table {...table} showSearchBar={false} showColumnToggler={false} getCoreRowModel={getCoreRowModel()} />
      </Paper>
    </>
  );
};

export default DeviceTypeSettings;
